import api from "@/services/api.ts";
import {
    Mentee,
    MenteeWithPreferenceAndSuggestionsDetail,
    UpdateMenteeMetaDto,
    UpdateMenteeWithMentorIdDto
} from "@/types/Mentee.ts";

const URL = '/mentees';

export const getMentees = async (seasonId?: number): Promise<Mentee[]> => {
    const suffix = seasonId ? `?seasonId=${seasonId}` : "";
    const {data} = await api.get<Mentee[]>(`${URL}${suffix}`);
    return data;
};

export type UnmatchedDiagnosisEntry = {
    menteeId: number;
    fullName: string;
    email: string;
    reason: "no_preference" | "no_suggestions" | "not_selected";
    hasPreference: boolean;
    hasSuggestions: boolean;
    preferenceMatched: boolean | null;
};

export type UnmatchedDiagnosis = {
    seasonId: number;
    totalUnmatched: number;
    reasons: {
        no_preference: number;
        no_suggestions: number;
        not_selected: number;
    };
    entries: UnmatchedDiagnosisEntry[];
};

export const getUnmatchedDiagnosis = async (seasonId?: number): Promise<UnmatchedDiagnosis> => {
    const suffix = seasonId ? `?seasonId=${seasonId}` : "";
    const {data} = await api.get<UnmatchedDiagnosis>(`${URL}/unmatched-diagnosis${suffix}`);
    return data;
};

export type MenteeBreakdownBucket = { label: string; count: number; prevCount?: number };

export type MenteeBreakdownStats = {
    byStudentStatus: MenteeBreakdownBucket[];
    byDiscipline: MenteeBreakdownBucket[];
    byRegion: MenteeBreakdownBucket[];
    totalCurrent: number;
    totalPrev?: number;
};

export const getMenteeBreakdownStats = async (seasonId?: number): Promise<MenteeBreakdownStats> => {
    const suffix = seasonId ? `?seasonId=${seasonId}` : "";
    const {data} = await api.get<MenteeBreakdownStats>(`${URL}/breakdown${suffix}`);
    return data;
};

export const updateMenteeWithMetadata = async (id: number, mentee: Partial<UpdateMenteeMetaDto>): Promise<Mentee> => {
    try {
        const {data} = await api.put(`${URL}/${id}`, mentee);
        return data;
    } catch (error) {
        throw new Error('Failed to update mentee.');
    }
};

export const updateMenteeWithMentor = async (id: number, mentee: UpdateMenteeWithMentorIdDto): Promise<Mentee> => {
    try {
        const {data} = await api.put(`${URL}/${id}`, mentee);
        return data;
    } catch (error) {
        throw new Error('Failed to assign mentor to mentee.');
    }
};

export const getMenteeById = async (id: number): Promise<Mentee | null> => {
    try {
        const response = await api.get(`${URL}/${id}`);
        return response.data;
    } catch (error: any) {
        if (error.response && error.response.status === 404) {
            return null;
        }
        throw error;
    }
};

export const getMenteeByIdWithSuggestedMentors = async (menteeId: number): Promise<MenteeWithPreferenceAndSuggestionsDetail> => {
    const response = await api.get<MenteeWithPreferenceAndSuggestionsDetail>(`${URL}/${menteeId}/suggested-mentors`);
    return response.data;
};

export const getMenteesWithSuggestedMentors = async (): Promise<MenteeWithPreferenceAndSuggestionsDetail[]> => {
    const response = await api.get<MenteeWithPreferenceAndSuggestionsDetail[]>(`${URL}/suggested-mentors`);
    return response.data;
};

export const unassignMentor = async (menteeId: number): Promise<Mentee> => {
    try {
        const {data} = await api.get(`${URL}/${menteeId}/unassign-mentor`);
        return data;
    } catch (error) {
        throw new Error('Failed to unassign mentee from mentor.');
    }
};

export const getMenteeByIdWithAllMentorChoices = async (menteeId: number): Promise<MenteeWithPreferenceAndSuggestionsDetail> => {
    const response = await api.get<MenteeWithPreferenceAndSuggestionsDetail>(`${URL}/${menteeId}/all-mentor-choices`);
    return response.data;
};

export type MenteeSessionItem = {
    sessionId: number;
    sessionDate: string;
    sessionTime: string;
    mentorName: string;
    hasMentorNote: boolean;
    hasMenteeFeedback: boolean;
};

export type MenteeSessionsForSeason = {
    attended: MenteeSessionItem[];
    missed: MenteeSessionItem[];
    upcoming: MenteeSessionItem[];
};

export const getMenteeSessions = async (menteeId: number): Promise<MenteeSessionsForSeason> => {
    const { data } = await api.get<MenteeSessionsForSeason>(`${URL}/${menteeId}/sessions`);
    return data;
};

export type MenteePreferenceStats = {
    completedCount: number;
    totalMentees: number;
    percentChangeToday: number;
    timeSeries: { day: string; total: number }[];
};

/** Fetches mentee preference submission stats for the dashboard (real data from API). */
export const getMenteePreferenceStats = async (): Promise<MenteePreferenceStats> => {
    const { data } = await api.get<MenteePreferenceStats>(`${URL}/preference-stats`);
    return data;
};

export type MentorSelectionStats = {
    completedCount: number;
    totalMentees: number;
    percentChangeToday: number;
    timeSeries: { day: string; total: number }[];
};

/** Fetches day-by-day mentor selection counts for the admin dashboard. */
export const getMentorSelectionStats = async (): Promise<MentorSelectionStats> => {
    const { data } = await api.get<MentorSelectionStats>(`${URL}/mentor-selection-stats`);
    return data;
};
